import i18n from "i18next";
import { initReactI18next } from "react-i18next";


export const LANGUAGES = [
  { code: "en", flag: "🇬🇧", locale: "en-US" },
  { code: "hu", flag: "🇭🇺", locale: "hu-HU" },
  { code: "sr", flag: "🇷🇸", locale: "sr-Latn-RS" },
];
export const SUPPORTED = LANGUAGES.map((l) => l.code);
export const DEFAULT_LANGUAGE = "en";
export const STORAGE_KEY = "malilabs-lang";

/** "hu-HU" / "sr-Latn-RS" -> "hu" / "sr"; anything unsupported -> "en" */
export const baseLang = (lng) => {
  const code = String(lng || "").toLowerCase().split(/[-_]/)[0];
  return SUPPORTED.includes(code) ? code : DEFAULT_LANGUAGE;
};

/** Remember a deliberate language choice (read by LangLayout on the next visit to a root URL). */
export const rememberLanguage = (code) => {
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch {
    /* storage blocked */
  }
};

export const localeFor = (lng) => (LANGUAGES.find((l) => l.code === baseLang(lng)) || LANGUAGES[0]).locale;

// The URL decides the first render's language ("/hu/..." -> hu, "/sr/..." -> sr,
// anything else -> en), so it matches the prerendered HTML that hydrateRoot
// adopts. A remembered or browser language is applied afterwards by LangLayout,
// which redirects English URLs to the visitor's language.
const languageFromPath = (pathname) => {
  const m = String(pathname || "").match(/^\/(hu|sr)(\/|$)/);
  return m ? m[1] : DEFAULT_LANGUAGE;
};

// Each locale is its own chunk (~40 KB of strings apiece). A visitor downloads
// only the language in the URL; another one is fetched when they switch, and
// i18next waits for it before announcing the change, so no half-translated
// render happens. src/index.js awaits `i18nReady` before the first render.
const lazyLocales = {
  type: "backend",
  init() {},
  read(lng, _ns, callback) {
    import(/* webpackChunkName: "locale-[request]" */ `./locales/${baseLang(lng)}.json`)
      .then((m) => callback(null, m.default))
      .catch((err) => callback(err, null));
  },
};

export const i18nReady = i18n
  .use(lazyLocales)
  .use(initReactI18next)
  .init({
    lng: languageFromPath(typeof window !== "undefined" ? window.location.pathname : "/"),
    // scripts/build-seo.mjs fails the build if the locale files differ in keys, so
    // a fallback language would only cost a second download.
    fallbackLng: false,
    supportedLngs: SUPPORTED,
    nonExplicitSupportedLngs: true, // hu-HU -> hu, sr-Latn-RS -> sr
    load: "languageOnly",
    interpolation: { escapeValue: false }, // React already escapes
    returnObjects: true,
  });

const syncHtmlLang = (lng) => {
  document.documentElement.lang = baseLang(lng);
};
syncHtmlLang(i18n.language);
i18n.on("languageChanged", syncHtmlLang);

export default i18n;
